import React, { useState, useEffect } from "react";
import { PageHeader, Card, Badge, Button, Input } from "@/components/ui";
import { endpoints, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSearchParams } from "react-router-dom";
import { CostTracker } from "@/components/CostTracker";
import { SlidersHorizontal } from "lucide-react";

function DigestSettings() {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { endpoints.digestSetting().then((r) => setS(r.data)).catch(() => setS({ enabled: true })); }, []);
  const toggle = async () => {
    const next = !s.enabled;
    setS({ ...s, enabled: next });
    try { await endpoints.setDigestSetting(next); } catch { setS({ ...s, enabled: !next }); }
  };
  const sendMe = async () => {
    setBusy(true); setMsg("");
    try { const r = await endpoints.digestSendMe(); setMsg(`Digest sent to ${r.data.to}`); }
    catch (e) { setMsg("Error: " + (e?.response?.data?.detail || e.message)); } finally { setBusy(false); }
  };
  const showPreview = async () => {
    if (preview) return setPreview(null);
    const r = await endpoints.digestPreview();
    setPreview(r.data.html);
  };
  if (!s) return null;
  return (
    <Card hover={false} data-testid="settings-digest">
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="text-[14px] text-white">Weekly Digest email</div>
          <div className="text-[12px] text-white/45 mt-1 max-w-lg">Every Monday at 07:00 UTC: committee changes, top investor matches and pipeline moves from your workspace, on one page.</div>
          {s.last_digest_at && <div className="text-[10.5px] text-white/35 mt-1 font-mono">Last sent {String(s.last_digest_at).slice(0, 16).replace("T", " ")}</div>}
        </div>
        <button onClick={toggle} role="switch" aria-checked={!!s.enabled} data-testid="digest-toggle"
          className={`relative shrink-0 w-12 h-6 rounded-full transition-colors ${s.enabled ? "bg-[#C5A059]" : "bg-white/15"}`}>
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${s.enabled ? "translate-x-6" : ""}`} />
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <Button size="sm" variant="outline" onClick={showPreview} data-testid="digest-preview-btn">{preview ? "Hide preview" : "Preview"}</Button>
        <Button size="sm" onClick={sendMe} disabled={busy} data-testid="digest-send-me-btn">{busy ? "Sending…" : "Send me this week's digest"}</Button>
        {msg && <span className="text-[12px] text-gold self-center" data-testid="digest-msg">{msg}</span>}
      </div>
      {preview && <iframe title="digest" srcDoc={preview} className="w-full h-[520px] mt-4 rounded bg-white" data-testid="digest-preview-frame" />}
    </Card>
  );
}

function PlatformSettings() {
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    endpoints.getEnrichmentSetting().then((r) => setOn(!!r.data.background_enrichment_enabled)).catch(() => setOn(true));
  }, []);
  const toggle = async () => {
    const next = !on;
    setBusy(true); setOn(next);
    try { await endpoints.setEnrichmentSetting(next); }
    catch (e) { setOn(!next); alert("Failed to update: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="settings-platform">
      <Card hover={false}>
        <div className="overline mb-4">Platform</div>
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="text-[14px] text-white">Background dossier enrichment</div>
            <div className="text-[12px] text-white/45 mt-1 max-w-lg">When ON, every newly discovered target automatically gets an AI M&A dossier generated in the background (~2 LLM calls per target). Turn OFF to save cost; targets are still inserted.</div>
          </div>
          <button onClick={toggle} disabled={busy || on === null} role="switch" aria-checked={!!on} data-testid="bg-enrichment-toggle"
            className={`relative shrink-0 w-12 h-6 rounded-full transition-colors ${on ? "bg-[#C5A059]" : "bg-white/15"}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-6" : ""}`} />
          </button>
        </div>
        <div className="mt-3 text-[11px] font-mono" data-testid="bg-enrichment-state">
          Status: <span className={on ? "text-emerald-400" : "text-amber-400"}>{on === null ? "…" : on ? "ON" : "OFF"}</span>
        </div>
      </Card>
      <DigestSettings />
      <CostTracker scope="me" title="Your AI & search spend" />
    </div>
  );
}
import { UserCircle, Shield, Trash2, AlertTriangle, LogOut, Globe, CheckCircle2, XCircle, TrendingUp, Database, DollarSign, Zap } from "lucide-react";

export default function Settings() {
  const { user, logout } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "profile");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [tavily, setTavily] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (tab === "integrations") {
      api.get("/integrations/tavily/status").then((r) => setTavily(r.data)).catch(() => setTavily({ configured: false, key_present: false, package_installed: false }));
      api.get("/integrations/tavily/analytics").then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null));
    }
  }, [tab]);

  const handleDelete = async () => {
    if (deleteConfirm !== "DELETE") {
      alert("Type DELETE to confirm.");
      return;
    }
    setDeleting(true);
    try {
      await endpoints.deleteAccount();
      window.location.href = "/login";
    } catch (e) {
      alert("Delete failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setDeleting(false);
    }
  };

  const tabs = [
    { id: "profile", label: "Profile", icon: UserCircle },
    { id: "security", label: "Security", icon: Shield },
    { id: "platform", label: "Platform", icon: SlidersHorizontal },
    { id: "integrations", label: "Integrations", icon: Globe },
    { id: "account", label: "Account", icon: Trash2 },
  ];

  return (
    <div className="p-4 md:p-10 max-w-4xl mx-auto">
      <PageHeader eyebrow="Settings" title="Account" testid="settings-page" />

      <div className="flex border-b border-white/5 mb-6 overflow-x-auto no-scrollbar">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`settings-tab-${t.id}`}
            className={`px-5 py-3 text-sm border-b-2 transition whitespace-nowrap flex items-center gap-2 ${
              tab === t.id ? "border-[#C5A059] text-white" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "platform" && <PlatformSettings />}

      {tab === "profile" && (
        <Card>
          <div className="flex items-center gap-4 mb-6">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-16 h-16 rounded-full border-2 border-[#C5A059]/40" />
            ) : (
              <div className="w-16 h-16 rounded-full gradient-gold flex items-center justify-center text-black text-2xl font-display font-bold">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
            )}
            <div>
              <div className="font-display text-2xl">{user?.name || "User"}</div>
              <div className="text-sm text-white/50">{user?.email}</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="overline mb-1.5 block">Name</label>
              <Input value={user?.name || ""} readOnly />
            </div>
            <div>
              <label className="overline mb-1.5 block">Email</label>
              <Input value={user?.email || ""} readOnly />
            </div>
          </div>
        </Card>
      )}

      {tab === "security" && (
        <Card>
          <div className="overline mb-4">Authentication</div>
          <div className="flex items-center gap-3 p-4 clara-surface-2 rounded-md">
            <Shield size={20} className="text-gold" />
            <div className="flex-1">
              <div className="font-medium text-sm">Google OAuth</div>
              <div className="text-[12px] text-white/40">Session valid for 7 days · httpOnly cookie</div>
            </div>
            <Badge tone="green">Active</Badge>
          </div>
          <Button variant="outline" className="mt-4" onClick={logout} data-testid="signout-btn">
            <LogOut size={14} /> Sign out
          </Button>
        </Card>
      )}

      {tab === "integrations" && (
        <Card>
          <div className="overline mb-4">Web Search Provider</div>
          <div className="flex items-center gap-3 p-4 clara-surface-2 rounded-md mb-4" data-testid="tavily-status-card">
            <Globe size={20} className="text-gold shrink-0" />
            <div className="flex-1">
              <div className="font-medium text-sm flex items-center gap-2">
                Tavily Search API
                <Badge tone="gold">Primary</Badge>
              </div>
              <div className="text-[12px] text-white/40 mt-0.5">
                Used by CLARA Origin Discovery + Ask CLARA for real-time web evidence.
              </div>
            </div>
            {tavily?.configured ? (
              <Badge tone="green" data-testid="tavily-badge-active">
                <CheckCircle2 size={12} className="mr-1 inline" /> Active
              </Badge>
            ) : (
              <Badge tone="red" data-testid="tavily-badge-inactive">
                <XCircle size={12} className="mr-1 inline" /> Not Configured
              </Badge>
            )}
          </div>

          {tavily && !tavily.configured && (
            <div className="p-4 rounded-md text-sm" style={{ background: "rgba(197,160,89,0.06)", border: "1px solid rgba(197,160,89,0.25)" }}>
              <div className="font-medium mb-2 text-white/90">Setup required</div>
              <ol className="list-decimal ml-5 space-y-1.5 text-white/60 text-[13px]">
                <li>Sign up at <a href="https://app.tavily.com" target="_blank" rel="noreferrer" className="text-gold underline">app.tavily.com</a></li>
                <li>Copy your API key (starts with <span className="font-mono">tvly-…</span>)</li>
                <li>Set <span className="font-mono text-gold">TAVILY_API_KEY</span> in <span className="font-mono">/app/backend/.env</span></li>
                <li>Restart the backend</li>
              </ol>
              <div className="mt-3 text-[11px] text-white/40">
                Package installed: <span className={tavily.package_installed ? "text-emerald-400" : "text-red-400"}>{String(!!tavily.package_installed)}</span> · Key present: <span className={tavily.key_present ? "text-emerald-400" : "text-red-400"}>{String(!!tavily.key_present)}</span>
              </div>
            </div>
          )}

          {tavily?.configured && (
            <div className="p-4 rounded-md text-sm" style={{ background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.25)" }}>
              <div className="text-white/70">
                Tavily is live. All AI agents (Origin Discovery, Ask CLARA) now receive real-time web evidence with each query. Fallback: silent degradation if the API errors.
              </div>
            </div>
          )}

          {tavily?.configured && analytics && (
            <div className="mt-8" data-testid="tavily-analytics">
              <div className="overline mb-4 flex items-center gap-2">
                <TrendingUp size={12} className="text-gold" /> Search Analytics · Last 30 days
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="p-3 clara-surface-2 rounded-md">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/40 mb-1">
                    <Database size={11} /> Total Queries
                  </div>
                  <div className="text-2xl font-display text-white">{analytics.total_queries}</div>
                </div>
                <div className="p-3 clara-surface-2 rounded-md">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/40 mb-1">
                    <Zap size={11} className="text-gold" /> Cache Hit Rate
                  </div>
                  <div className="text-2xl font-display text-gold">{analytics.cache_hit_rate}%</div>
                  <div className="text-[10px] text-white/40 mt-0.5">{analytics.cached_queries} of {analytics.total_queries}</div>
                </div>
                <div className="p-3 clara-surface-2 rounded-md">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/40 mb-1">
                    <DollarSign size={11} className="text-red-400" /> Est. Spend
                  </div>
                  <div className="text-2xl font-display text-white">${analytics.estimated_cost_usd?.toFixed(2)}</div>
                </div>
                <div className="p-3 clara-surface-2 rounded-md" style={{ border: "1px solid rgba(34,197,94,0.3)" }}>
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-400/90 mb-1">
                    <DollarSign size={11} /> Cache Savings
                  </div>
                  <div className="text-2xl font-display text-emerald-400">${analytics.estimated_savings_usd?.toFixed(2)}</div>
                </div>
              </div>

              {/* 30-day timeseries mini chart */}
              <div className="p-3 clara-surface-2 rounded-md mb-4">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Query Volume · 30 days</div>
                <div className="flex gap-[2px] items-end h-16">
                  {analytics.last_30d_series.map((day) => {
                    const total = day.live + day.cached;
                    const max = Math.max(1, ...analytics.last_30d_series.map((d) => d.live + d.cached));
                    const h = Math.max(3, (total / max) * 100);
                    return (
                      <div key={day.date} title={`${day.date} · ${total} queries (${day.cached} cached)`} className="flex-1 flex flex-col justify-end gap-[1px]">
                        {day.cached > 0 && <div style={{ height: `${(day.cached / max) * 64}px`, background: "rgba(197,160,89,0.35)" }} />}
                        {day.live > 0 && <div style={{ height: `${(day.live / max) * 64}px`, background: "#C5A059" }} />}
                        {total === 0 && <div style={{ height: "3px", background: "rgba(255,255,255,0.05)" }} />}
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-4 mt-2 text-[10px] text-white/40">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: "#C5A059" }} /> Live</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: "rgba(197,160,89,0.35)" }} /> Cached</span>
                </div>
              </div>

              {/* Recent queries */}
              {analytics.recent_queries?.length > 0 && (
                <div className="p-3 clara-surface-2 rounded-md">
                  <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Recent Queries</div>
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {analytics.recent_queries.map((q, i) => (
                      <div key={i} className="flex items-center gap-2 text-[12px] py-1 border-b border-white/[0.03] last:border-0">
                        {q.cached ? (
                          <Badge tone="gold" className="!py-0 !px-1.5 !text-[9px]">CACHE</Badge>
                        ) : q.error ? (
                          <Badge tone="red" className="!py-0 !px-1.5 !text-[9px]">ERR</Badge>
                        ) : (
                          <Badge tone="green" className="!py-0 !px-1.5 !text-[9px]">LIVE</Badge>
                        )}
                        <span className="text-white/70 truncate flex-1">{q.query}</span>
                        <span className="text-white/30 text-[10px] font-mono shrink-0">{q.search_depth}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {tab === "account" && (
        <Card>
          <div className="flex items-start gap-3 p-4 rounded-md mb-6" style={{ background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.2)" }}>
            <AlertTriangle size={20} className="text-red-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-lg text-red-400 mb-1">Delete Account</div>
              <div className="text-sm text-white/60">
                This will permanently delete your account, sessions, deal records and chat history.
                This action cannot be undone.
              </div>
            </div>
          </div>

          <div className="space-y-3 max-w-md">
            <label className="text-[12px] text-white/50 block">
              Type <span className="font-mono text-red-400">DELETE</span> to confirm:
            </label>
            <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="DELETE" data-testid="delete-confirm-input" />
            <Button variant="danger" onClick={handleDelete} disabled={deleteConfirm !== "DELETE" || deleting} data-testid="delete-account-btn">
              <Trash2 size={14} /> {deleting ? "Deleting..." : "Permanently Delete Account"}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
